import {
  CheckCircleOutlined, ClockCircleOutlined, CustomerServiceOutlined, PauseCircleOutlined,
  PlayCircleOutlined, SafetyCertificateOutlined, ToolOutlined
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Empty, Segmented, Space, Spin, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PermissionCode } from "@autoflow/types";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatDateTime } from "../../shared/utils/formatters";
import { customerName } from "../customers/customer-types";
import { repairOrdersApi } from "../repair-orders/repair-orders-api";
import type { WorkshopQueueItem } from "../repair-orders/repair-order-types";
import { useAuth } from "../auth/AuthProvider";

type QueueFilter = "ACTIVE" | "WAITING" | "QC";

export function TechnicianQueuePage() {
  const { text } = useAppConfig();
  const t = text as unknown as Record<string, string>;
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<QueueFilter>("ACTIVE");
  const [tick, setTick] = useState(Date.now());
  const queue = useQuery({
    queryKey: ["workshop-queue"],
    queryFn: () => repairOrdersApi.workshopQueue(),
    refetchInterval: 15_000
  });
  const action = useMutation({
    mutationFn: async ({ item, action: next }: { item: WorkshopQueueItem; action: string }) => {
      if (next === "start") return repairOrdersApi.startServiceWork(item.repairOrder.id, item.id);
      if (next === "pause") return repairOrdersApi.pauseServiceWork(item.repairOrder.id, item.id);
      if (next === "resume") return repairOrdersApi.resumeServiceWork(item.repairOrder.id, item.id);
      if (next === "complete") return repairOrdersApi.completeServiceWork(item.repairOrder.id, item.id);
      return repairOrdersApi.serviceQc(item.repairOrder.id, item.id, next === "qc-pass");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workshop-queue"] });
    }
  });
  useEffect(() => {
    const timer = window.setInterval(() => setTick(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const items = useMemo(() => (queue.data?.items ?? []).filter(item => {
    if (filter === "WAITING") return item.nextAction === "WAIT_CUSTOMER_APPROVAL" || item.nextAction === "ISSUE_PARTS";
    if (filter === "QC") return item.nextAction === "PERFORM_QC" || item.nextAction === "QC_COMPLETE";
    return !["WAIT_CUSTOMER_APPROVAL", "PERFORM_QC", "QC_COMPLETE"].includes(item.nextAction);
  }), [filter, queue.data?.items]);

  if (queue.isLoading) return <div className="workspace-loading"><Spin size="large" /></div>;

  return <main className="technician-queue-page">
    <header className="technician-queue-header">
      <div>
        <Typography.Text type="secondary">{t.workshopOperations}</Typography.Text>
        <Typography.Title level={2}>{t.technicianQueue}</Typography.Title>
        <Typography.Paragraph type="secondary">{t.technicianQueueHint}</Typography.Paragraph>
      </div>
      <Tag color="blue">{queue.data?.scope === "MY_QUEUE" ? t.myQueue : t.shopQueue}</Tag>
    </header>
    {action.isError && <Alert type="error" showIcon message={String(action.error)} />}
    <div className="technician-queue-toolbar">
      <Segmented
        value={filter}
        onChange={value => setFilter(value as QueueFilter)}
        options={[
          { value: "ACTIVE", label: t.activeServices },
          { value: "WAITING", label: t.waitingAttention },
          { value: "QC", label: t.qcQueue }
        ]}
      />
      <Typography.Text type="secondary">
        {t.liveUpdated} · {queue.data?.generatedAt ? formatDateTime(queue.data.generatedAt) : "—"}
      </Typography.Text>
    </div>
    <section className="technician-queue-grid">
      {items.length === 0
        ? <Empty description={t.noServicesInQueue} />
        : items.map(item => <QueueServiceCard
          key={item.id}
          item={item}
          t={t}
          tick={tick}
          pending={action.isPending}
          canLabor={user?.permissions.includes(PermissionCode.LaborWrite) ?? false}
          canQc={user?.permissions.includes(PermissionCode.RepairOrderWrite) ?? false}
          onAction={next => action.mutate({ item, action: next })}
          onOpen={() => navigate(`/repair-orders/${item.repairOrder.id}/workspace#services`)}
        />)}
    </section>
  </main>;
}

function QueueServiceCard({
  item, t, tick, pending, canLabor, canQc, onAction, onOpen
}: {
  item: WorkshopQueueItem;
  t: Record<string, string>;
  tick: number;
  pending: boolean;
  canLabor: boolean;
  canQc: boolean;
  onAction: (action: string) => void;
  onOpen: () => void;
}) {
  const liveSeconds = item.workSession?.status === "RUNNING" && item.workSession.runningSince
    ? item.workSession.accumulatedSeconds
      + Math.max(0, Math.floor((tick - new Date(item.workSession.runningSince).getTime()) / 1000))
    : item.elapsedSeconds;
  return <Card className={`queue-service-card queue-${item.nextAction.toLowerCase()}`}>
    <div className="queue-service-topline">
      <Space wrap>
        <Tag color={priorityColor(item.repairOrder.priority)}>{t[`priority${item.repairOrder.priority}`] ?? item.repairOrder.priority}</Tag>
        <Typography.Text strong>{item.repairOrder.roNumber}</Typography.Text>
      </Space>
      <Tag color={nextActionColor(item.nextAction)}>{t[`queueAction${item.nextAction}`] ?? item.nextAction}</Tag>
    </div>
    <Typography.Title level={3}>{item.serviceCatalog.serviceName}</Typography.Title>
    <Typography.Paragraph type="secondary">
      {item.repairOrder.vehicle.year} {item.repairOrder.vehicle.make} {item.repairOrder.vehicle.model}
      {" · "}{customerName(item.repairOrder.customer)}
    </Typography.Paragraph>
    <div className="queue-service-facts">
      <span><ClockCircleOutlined /><small>{t.promisedAt}</small><b>{item.repairOrder.promisedAt ? formatDateTime(item.repairOrder.promisedAt) : "—"}</b></span>
      <span><ToolOutlined /><small>{t.technician}</small><b>{item.technician?.displayName ?? t.unassigned}</b></span>
      <span><CustomerServiceOutlined /><small>{t.partsReadiness}</small><b>{item.partsReady ? t.ready : t.queueActionISSUE_PARTS}</b></span>
      <span><SafetyCertificateOutlined /><small>{t.qc}</small><b>{t[`serviceQc${item.qcStatus}`] ?? item.qcStatus}</b></span>
    </div>
    <div className="queue-live-clock">
      <ClockCircleOutlined />
      <div><small>{t.actualWorkTime}</small><b>{formatDuration(liveSeconds)}</b></div>
      {item.workSession && <Tag color={item.workSession.status === "RUNNING" ? "green" : item.workSession.status === "PAUSED" ? "orange" : "blue"}>
        {t[`workSession${item.workSession.status}`] ?? item.workSession.status}
      </Tag>}
    </div>
    <div className="queue-service-actions">
      <Button onClick={onOpen}>{t.openRepairEvent}</Button>
      {canLabor && item.nextAction === "START_SERVICE" && <Button type="primary" icon={<PlayCircleOutlined />} loading={pending} onClick={() => onAction("start")}>{t.start}</Button>}
      {canLabor && item.nextAction === "CONTINUE_SERVICE" && <Button icon={<PauseCircleOutlined />} loading={pending} onClick={() => onAction("pause")}>{t.pause}</Button>}
      {canLabor && item.nextAction === "RESUME_SERVICE" && <Button type="primary" icon={<PlayCircleOutlined />} loading={pending} onClick={() => onAction("resume")}>{t.resume}</Button>}
      {canLabor && ["CONTINUE_SERVICE", "RESUME_SERVICE"].includes(item.nextAction) && <Button type="primary" icon={<CheckCircleOutlined />} loading={pending} onClick={() => onAction("complete")}>{t.complete}</Button>}
      {canQc && item.nextAction === "PERFORM_QC" && <>
        <Button danger loading={pending} onClick={() => onAction("qc-fail")}>{t.qcFail}</Button>
        <Button type="primary" icon={<CheckCircleOutlined />} loading={pending} onClick={() => onAction("qc-pass")}>{t.qcPass}</Button>
      </>}
    </div>
  </Card>;
}

function formatDuration(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  return [hours, minutes, remainder].map(value => String(value).padStart(2, "0")).join(":");
}

function priorityColor(priority: string) {
  return priority === "URGENT" ? "red" : priority === "HIGH" ? "orange" : priority === "LOW" ? "default" : "blue";
}

function nextActionColor(action: string) {
  if (action === "WAIT_CUSTOMER_APPROVAL" || action === "ISSUE_PARTS") return "orange";
  if (action === "PERFORM_QC") return "cyan";
  if (action === "QC_COMPLETE") return "green";
  return "blue";
}
